// Parsing for the IRA search form's single free-text "Full Name" field.
//
// The API matches on firstName + lastName: node-api's riskAssessCT queries the
// CT judicial site by last name + first initial + birth year and ignores
// middleName entirely. So whatever lands in lastName *is* the search, and a bad
// lastName silently returns a confident report about the wrong person.
//
// Two ways the old `split(' ')` + first/last token approach got that wrong:
//   "Nelson Walker Jr"  -> firstName "Nelson", lastName "Jr"  (suffix as surname)
//   "Nelson Walker "    -> firstName "Nelson", lastName ""    (empty trailing token)
// The second is why reports came back saved under a first name only.

// Generational suffixes, compared lowercased with trailing dots/commas removed.
// These must never become the surname. Roman numerals stay in the list even
// though "V" could in theory be a middle initial, because a trailing lone
// initial is no more useful as a lastName than a suffix is.
const NAME_SUFFIXES = new Set([
    'jr', 'sr', 'ii', 'iii', 'iv', 'v', 'vi',
    '2nd', '3rd', '4th',
]);

// Users type "Nelson Walker, Jr." as often as "Nelson Walker Jr", so punctuation
// is stripped off the token edges before anything else looks at it.
const stripEdgePunctuation = (token: string): string => token.replace(/^[,.\s]+|[,.\s]+$/g, '');

const isSuffix = (token: string): boolean => NAME_SUFFIXES.has(token.toLowerCase());

export interface ParsedPersonName {
    firstName: string;
    middleName: string;
    lastName: string;
}

// Splits a free-text full name into the fields the IRA API expects.
//
// Compound surnames keep the pre-existing behaviour: the last token is the
// surname, so "Maria de la Cruz" gives lastName "Cruz" and middleName "de la".
// That is right for the CT judicial lookup (its records store the bare surname)
// and harmless for the address-based news search (unquoted query, and the
// article filter matches first and last independently). It is a coin flip
// against Crimeometer's exact `last_name` field, which may hold either form.
// Resolving that needs surname-variant expansion in node-api, mirroring the
// first-name alias expansion already there, so it is left alone here.
export const parsePersonName = (fullName: string): ParsedPersonName => {
    const empty: ParsedPersonName = { firstName: '', middleName: '', lastName: '' };
    if (!fullName) return empty;

    // Collapses runs of whitespace and drops the empty tokens that a trailing
    // or doubled space used to leave behind.
    const tokens = fullName
        .split(/\s+/)
        .map(stripEdgePunctuation)
        .filter((token) => token.length > 0);

    // Trailing suffixes are dropped from the name entirely. The API has no
    // field for them and they aren't part of either searchable name.
    while (tokens.length > 0 && isSuffix(tokens[tokens.length - 1])) {
        tokens.pop();
    }

    if (tokens.length === 0) return empty;

    // One token is ambiguous (the form asks for both names but doesn't enforce
    // it). Mirroring it into both fields is the long-standing behaviour: the CT
    // search needs a lastName to return anything at all.
    if (tokens.length === 1) {
        return { firstName: tokens[0], middleName: '', lastName: tokens[0] };
    }

    return {
        firstName: tokens[0],
        middleName: tokens.slice(1, -1).join(' '),
        lastName: tokens[tokens.length - 1],
    };
};
